import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CalendarClock, Fingerprint, AlarmClock, TrendingUp, TrendingDown, Minus, LineChart as LineIcon, BarChart3 } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip, ReferenceLine } from "recharts";
import { api } from "@/lib/api";
import { StatusBadge, LeagueChip, CategoryTag, SourceBadge } from "@/components/badges";
import { relativeTime, absoluteTime, kickoffLabel, minutesUntil, returnCountdown } from "@/lib/format";
import { WatchlistButton } from "@/components/WatchlistButton";

const IDENTITY_LABELS = { roster_grid: "Roster Grid", market_watch: "Market Watch", media_desk: "Media Desk" };

export default function PlayerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/players/${id}`);
      setData(data);
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (notFound) return (
    <div className="max-w-[700px] mx-auto p-10 text-center">
      <p className="font-heading text-xl font-bold text-slate-100">Player not found</p>
      <Link to="/feed" data-testid="notfound-back" className="text-[#00E5A3] hover:underline text-sm mt-2 inline-block">← Back to the live feed</Link>
    </div>
  );
  if (!data) return <div className="pt-24 text-center text-slate-400 font-mono">Loading player…</div>;
  const { player, timeline, odds_history = [], books = [], stats = null, matchup = null, splits = null } = data;
  const mins = minutesUntil(player.next_game?.kickoff_utc);
  const inLateWindow = mins != null && mins > 0 && mins <= 60 && player.status !== "active";
  const rc = returnCountdown(player.return_date);
  const statusHistory = (timeline || []).filter((ev) => ["injury", "suspension", "trade"].includes(ev.category));

  return (
    <div className="max-w-[1100px] mx-auto p-4 sm:p-6">
      <Link to="/feed" data-testid="back-to-feed" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100 mb-4">
        <ArrowLeft size={15} /> Back to feed
      </Link>

      {inLateWindow && (
        <div data-testid="late-window-banner" className="mb-4 rounded-xl border border-[#FF4D4D]/40 bg-[#FF4D4D]/10 px-4 py-3 flex items-center gap-3">
          <AlarmClock size={18} className="text-[#FF4D4D] shrink-0" />
          <p className="text-sm text-slate-200">
            <span className="font-semibold text-[#FF7A7A]">Late window · {mins} min to game.</span> This {player.status.toUpperCase()} player is in the 30–60 min window where most line movement happens — verify the latest report below.
          </p>
        </div>
      )}

      <div className="bg-[#0F1420] border border-[#232D42] rounded-2xl p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <LeagueChip league={player.league} />
              <StatusBadge status={player.status} />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-50 tracking-tight">{player.name}</h1>
            <p className="text-slate-400 mt-1 font-mono text-sm">#{player.jersey} · {player.position} · {player.team}</p>
            {player.status_note && <p className="text-sm text-[#FFB020] mt-3">{player.status_note}</p>}
            {player.status !== "active" && rc && (
              <p data-testid="player-return-countdown" className="text-sm mt-1 font-mono flex items-center gap-1.5"
                style={{ color: rc.soon ? "#FFB020" : "#94A3B8" }}>
                <CalendarClock size={13} /> {rc.label}
              </p>
            )}
          </div>
          <WatchlistButton player={player} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <div className="bg-[#151B2B] border border-[#232D42] rounded-xl p-4">
            <div className="flex items-center gap-2 text-slate-400 text-xs font-mono uppercase tracking-wider mb-2"><CalendarClock size={13} /> Next Game</div>
            {matchup ? (
              <Link to={`/games/${matchup.league}/${matchup.event_id}`} data-testid="player-next-game" className="group block">
                <p className="text-slate-100 font-semibold group-hover:text-[#00E5A3]">{matchup.home_away} {matchup.opponent?.name}
                  {matchup.opponent?.record && <span className="text-slate-500 font-mono text-xs ml-1">{matchup.opponent.record}</span>}
                </p>
                <p className="text-slate-500 text-sm font-mono">{kickoffLabel(matchup.date)}{matchup.odds?.details ? ` · ${matchup.odds.details}` : ""}</p>
                <span className="text-[11px] font-mono text-[#00E5A3] group-hover:underline">view full matchup →</span>
              </Link>
            ) : <p className="text-slate-500 text-sm font-mono">No upcoming game scheduled.</p>}
          </div>
          <div className="bg-[#151B2B] border border-[#232D42] rounded-xl p-4">
            <div className="flex items-center gap-2 text-slate-400 text-xs font-mono uppercase tracking-wider mb-2"><Fingerprint size={13} /> Cross-Source Identity</div>
            <div className="space-y-1">
              {Object.entries(player.external_ids || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs font-mono">
                  <span className="text-slate-500">{IDENTITY_LABELS[k] || k}</span><span className="text-slate-300">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <StatsSection stats={stats} />

      <SplitsSection splits={splits} />

      <MarketSection player={player} odds_history={odds_history} books={books} />

      {statusHistory.length > 0 && (
        <div className="mt-8" data-testid="injury-history">
          <h2 className="font-heading text-xl font-bold text-slate-100 mb-1">Injury &amp; Status History</h2>
          <p className="text-sm text-slate-500 mb-4">A running log of this player's status changes — each entry links to the original source.</p>
          <div className="relative pl-5 space-y-3 before:absolute before:left-1.5 before:top-1 before:bottom-1 before:w-px before:bg-[#232D42]">
            {statusHistory.map((ev) => (
              <div key={ev.id} data-testid={`history-event-${ev.id}`} className="relative">
                <span className="absolute -left-[13px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#00E5A3] ring-4 ring-[#0B0F18]" />
                <div className="bg-[#0F1420] border border-[#232D42] rounded-xl p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <CategoryTag category={ev.category} />
                    {ev.severity && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-[#FFB020]/15 text-[#FFB020]">{ev.severity}</span>}
                    <span className="ml-auto text-[11px] font-mono text-slate-500">{relativeTime(ev.timestamp_utc)}</span>
                  </div>
                  <p className="font-heading font-semibold text-slate-100">{ev.headline}</p>
                  {ev.detail && <p className="text-sm text-slate-400 mt-1">{ev.detail}</p>}
                  <div className="flex items-center gap-3 mt-2 text-[11px] font-mono text-slate-600">
                    <span>{absoluteTime(ev.timestamp_utc)}</span>
                    {ev.source_url && (
                      <a href={ev.source_url} target="_blank" rel="noopener noreferrer" className="text-[#38BDF8] hover:underline flex items-center gap-1">
                        {ev.source || "Source"} ↗
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="font-heading text-xl font-bold text-slate-100 mt-8 mb-4">News &amp; Status Timeline</h2>
      <div className="space-y-3" data-testid="player-timeline">
        {timeline.length === 0 && <div className="text-slate-500 font-mono text-sm">No events recorded yet.</div>}
        {timeline.map((ev) => (
          <div key={ev.id} data-testid={`timeline-event-${ev.id}`} className="bg-[#0F1420] border border-[#232D42] rounded-xl p-4">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <CategoryTag category={ev.category} />
              <SourceBadge official={ev.official} source={ev.source} />
              <span className="ml-auto text-[11px] font-mono text-slate-500">{relativeTime(ev.timestamp_utc)} · {absoluteTime(ev.timestamp_utc)}</span>
            </div>
            <p className="font-heading font-semibold text-slate-100">{ev.headline}</p>
            <p className="text-sm text-slate-400 mt-1">{ev.detail}</p>
            {ev.source_url && (
              <a href={ev.source_url} target="_blank" rel="noopener noreferrer" data-testid={`timeline-cite-${ev.id}`}
                className="text-[11px] font-mono text-[#38BDF8] hover:underline mt-2 inline-flex items-center gap-1">
                {ev.source || "Source"} ↗
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MarketSection({ player, odds_history, books }) {
  const open = Number(player.line_open ?? player.line);
  const current = Number(player.line);
  const delta = Math.round((current - open) * 10) / 10;
  const down = delta < 0;
  const chart = [{ i: 0, line: open }, ...odds_history.map((o, idx) => ({ i: idx + 1, line: o.line }))];
  if (chart.length === 1) chart.push({ i: 1, line: current });
  const Icon = down ? TrendingDown : TrendingUp;
  const color = delta === 0 ? "#94A3B8" : down ? "#FF4D4D" : "#00E5A3";

  return (
    <div className="mt-6" data-testid="market-section">
      <h2 className="font-heading text-xl font-bold text-slate-100 mb-1 flex items-center gap-2"><LineIcon size={18} className="text-[#38BDF8]" /> Market Context
        <span data-testid="market-sample-badge" className="text-[10px] font-mono uppercase tracking-wider text-[#F5A524] bg-[#F5A524]/10 border border-[#F5A524]/30 rounded px-1.5 py-0.5">Sample</span>
      </h2>
      <p className="text-sm text-slate-500 mb-4">Illustrative player-prop lines &amp; cross-book movement — this widget uses sample data until a live market/props feed is connected. Real game odds are on the <Link to="/live" className="text-[#00E5A3] hover:underline">Live Lines</Link> page. <span className="text-slate-600">Information only, not a recommendation.</span></p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 bg-[#0F1420] border border-[#232D42] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500">{player.market}</p>
              <div className="flex items-end gap-2 mt-1">
                <span className="font-heading text-3xl font-extrabold text-slate-50">{current}</span>
                <span className="mb-1 flex items-center gap-1 text-sm font-mono font-semibold" style={{ color }}>
                  <Icon size={14} /> {delta === 0 ? "no move" : `${delta > 0 ? "+" : ""}${delta} from open`}
                </span>
              </div>
              <p className="text-xs font-mono text-slate-600 mt-0.5">opened at {open}</p>
            </div>
          </div>
          <div className="h-28">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
                <YAxis domain={["dataMin - 1", "dataMax + 1"]} hide />
                <Tooltip contentStyle={{ background: "#151B2B", border: "1px solid #232D42", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#94A3B8" }} formatter={(v) => [v, player.market]} labelFormatter={() => ""} />
                <Line type="monotone" dataKey="line" stroke={color} strokeWidth={2.5} dot={{ r: 2, fill: color }} activeDot={{ r: 4 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-5 bg-[#0F1420] border border-[#232D42] rounded-xl p-5">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Cross-Book Comparison</p>
          <div className="space-y-1.5" data-testid="cross-book-table">
            {books.map((b) => (
              <div key={b.book} data-testid={`book-row-${b.book}`}
                className={`flex items-center justify-between rounded-lg px-3 py-2 ${b.lagging ? "border border-[#FFB020]/40 bg-[#FFB020]/10" : "bg-[#151B2B]"}`}>
                <span className="text-sm text-slate-200">{b.book}</span>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-slate-300">{b.line}</span>
                  <span className="font-mono text-xs text-slate-500 w-10 text-right">{b.odds > 0 ? `+${b.odds}` : b.odds}</span>
                  {b.lagging && <span className="text-[10px] font-mono uppercase tracking-wider text-[#FFB020]">lagging</span>}
                </div>
              </div>
            ))}
          </div>
          {books.some((b) => b.lagging) && (
            <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">A book still showing the opening line hasn't adjusted to the latest report yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}


const TREND_ICON = { up: TrendingUp, down: TrendingDown, flat: Minus };

function SplitsSection({ splits }) {
  if (!splits || !splits.rows?.length) return null;
  return (
    <div data-testid="splits-section" className="mt-8">
      <h2 className="font-heading text-xl font-bold text-slate-100 mb-1 flex items-center gap-2">
        <BarChart3 size={18} className="text-[#38BDF8]" /> Situational Splits
        <span className="text-[10px] font-mono uppercase tracking-wider text-[#00E5A3] bg-[#00E5A3]/10 border border-[#00E5A3]/30 rounded px-1.5 py-0.5">Real · ESPN</span>
      </h2>
      <p className="text-sm text-slate-500 mb-3">How this player performs by situation this season — home vs road, conference matchups and on rest.</p>
      <div className="overflow-x-auto rounded-xl border border-[#232D42] bg-[#0F1420]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500 border-b border-[#232D42]">
              <th className="text-left px-4 py-2">Split</th>
              {splits.labels.map((l) => <th key={l} className="text-right px-3 py-2">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {splits.rows.map((r) => (
              <tr key={r.split} data-testid={`split-row-${r.split}`} className="border-b border-[#232D42]/50 last:border-0">
                <td className="px-4 py-2 text-slate-200 whitespace-nowrap">{r.split}</td>
                {r.stats.map((s, i) => <td key={i} className="text-right px-3 py-2 font-mono text-slate-300">{s}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ m }) {
  const Icon = TREND_ICON[m.trend];
  const lib = m.lower_is_better;
  const good = m.trend === "flat" ? null : (lib ? m.trend === "down" : m.trend === "up");
  const color = good === null ? "#94A3B8" : good ? "#00E5A3" : "#FF4D4D";
  const chart = m.last10.map((v, i) => ({ i, v }));
  const hitPct = Math.round((m.hits / m.games) * 100);
  const hitColor = hitPct >= 60 ? "#00E5A3" : hitPct <= 40 ? "#FF4D4D" : "#FFB020";
  return (
    <div data-testid={`stat-card-${m.market}`} className="bg-[#0F1420] border border-[#232D42] rounded-xl p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-100 truncate">{m.market}</p>
          <p className="text-[11px] font-mono text-slate-500">Line {m.line}{m.unit === "%" ? "%" : ""}</p>
        </div>
        <span className="flex items-center gap-1 text-xs font-mono font-semibold shrink-0" style={{ color }}>
          <Icon size={13} /> {m.trend}
        </span>
      </div>

      <div className="flex items-end gap-1.5 mt-2">
        <span className="font-heading text-2xl font-extrabold text-slate-50">{m.l10_avg}</span>
        <span className="text-[11px] font-mono text-slate-500 mb-1">L10 avg · L5 {m.l5_avg}</span>
      </div>

      <div className="h-10 mt-1 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chart} margin={{ top: 3, right: 4, bottom: 0, left: 4 }}>
            <YAxis domain={["dataMin - 1", "dataMax + 1"]} hide />
            <ReferenceLine y={m.line} stroke="#475569" strokeDasharray="3 3" />
            <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2">
        <div className="flex items-center justify-between text-[11px] font-mono mb-1">
          <span className="text-slate-400">{lib ? "Under" : "Over"} in {m.hits} of {m.games}</span>
          <span style={{ color: hitColor }}>{hitPct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#232D42] overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${hitPct}%`, background: hitColor }} />
        </div>
      </div>

      <div className="mt-3 pt-2 border-t border-[#232D42] flex items-center justify-between gap-1">
        {m.season_history.slice().reverse().map((y, idx, arr) => {
          const prev = idx > 0 ? arr[idx - 1].avg : null;
          const better = prev != null && (lib ? y.avg < prev : y.avg > prev);
          return (
            <div key={y.year} className="text-center flex-1">
              <p className="text-[10px] font-mono text-slate-500">{y.year}</p>
              <p className="text-xs font-mono font-semibold" style={{ color: idx === arr.length - 1 ? "#F8FAFC" : better ? "#00E5A3" : "#94A3B8" }}>{y.avg}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatsSection({ stats }) {
  if (!stats || !stats.markets?.length) return null;
  const markets = stats.markets;
  return (
    <div className="mt-8" data-testid="stats-section">
      <h2 className="font-heading text-xl font-bold text-slate-100 mb-1 flex items-center gap-2"><BarChart3 size={18} className="text-[#00E5A3]" /> Recent Form &amp; Trends</h2>
      <p className="text-sm text-slate-500 mb-4">Top {markets.length} stats — last-10-game averages, how often they beat the line, the direction they're trending, and how this year stacks up against seasons past.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {markets.map((m) => <StatCard key={m.market} m={m} />)}
      </div>

      <div className="mt-6 bg-[#0F1420] border border-[#232D42] rounded-xl p-4">
        <p className="font-heading font-semibold text-slate-100 mb-3">Last 10 Games</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]" data-testid="game-log-table">
            <thead>
              <tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500 border-b border-[#232D42]">
                <th className="text-left py-2 pr-3 font-medium">Game</th>
                <th className="text-left py-2 pr-3 font-medium">Opp</th>
                {markets.map((m) => <th key={m.market} className="text-right py-2 px-2 font-medium whitespace-nowrap">{m.market}</th>)}
              </tr>
            </thead>
            <tbody>
              {stats.game_log.map((g, gi) => (
                <tr key={`${g.date}-${g.opponent}-${gi}`} className="border-b border-[#232D42]/50 hover:bg-[#151B2B]">
                  <td className="py-2 pr-3 whitespace-nowrap">
                    <span className="text-slate-300 font-mono text-xs">{g.date}</span>
                    <span className="ml-1.5 text-[10px] font-mono font-bold" style={{ color: g.result === "W" ? "#00E5A3" : "#FF4D4D" }}>{g.result}</span>
                  </td>
                  <td className="py-2 pr-3 text-slate-400 font-mono text-xs whitespace-nowrap">{g.opponent}</td>
                  {markets.map((m) => {
                    const v = g.stats[m.market];
                    const favorable = m.lower_is_better ? v < m.line : v > m.line;
                    return (
                      <td key={m.market} className="text-right py-2 px-2 font-mono">
                        <span style={{ color: favorable ? "#00E5A3" : "#94A3B8", fontWeight: favorable ? 700 : 400 }}>{v}</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-500 mt-3 font-mono">Green = beat the listed line that game. Information only — no predictions or recommendations.</p>
      </div>
    </div>
  );
}
